import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAdminStore } from '../../stores/adminStore';
import { useTranslation } from '../../hooks/useTranslation';
import { format } from 'timeago.js';
import { JsonViewer, ConsoleLogButton } from '../../utils/adminFunctions';
import { LocalizedLink } from '../../components/LocalizedLink';

export const UserSettings: React.FC = () => {
  const { t } = useTranslation();
  const { uid } = useParams<{ uid: string }>();
  const { 
    selectedUser, 
    userSettings, 
    userSettingsLoading, 
    userSettingsError, 
    fetchUserDetails, 
    fetchUserSettings 
  } = useAdminStore();

  useEffect(() => {
    if (!uid) return;
    
    fetchUserDetails(uid);
    fetchUserSettings(uid);
  }, [uid, fetchUserDetails, fetchUserSettings]);

  if (!uid) {
    return <div className="p-6 text-center text-red-500">{t('common.error')}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50/50 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <LocalizedLink
              to={`/admin`}
              className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('admin.backToAdmin')}
            </LocalizedLink>
            <h1 className="text-2xl font-semibold text-gray-900">
              {selectedUser ? `${t('admin.users.settings')} - ${selectedUser.email}` : t('admin.users.settings')}
            </h1>
            {selectedUser && (
              <p className="text-sm text-gray-500 mt-1">
                {t('admin.users.lastActive')}: {format(selectedUser.lastActive)}
              </p>
            )}
          </div>
          <div className="flex space-x-3">
            <LocalizedLink
              to={`/admin/user/${uid}/invoices`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('admin.users.viewInvoices')}
            </LocalizedLink>
            <LocalizedLink
              to={`/admin/user/${uid}/companies`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('admin.users.viewCompanies')}
            </LocalizedLink>
          </div>
        </div>

        {/* Settings Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100">
            <h2 className="text-lg font-medium text-gray-900">{t('admin.users.settings')}</h2>
          </div>
          
          {userSettingsLoading ? (
            <div className="p-6 text-center text-gray-500">{t('common.loading')}</div>
          ) : userSettingsError ? (
            <div className="p-6 text-center text-red-500">{userSettingsError}</div>
          ) : !userSettings ? (
            <div className="p-6 text-center text-gray-500">{t('admin.users.noSettings')}</div>
          ) : (
            <div className="p-6 space-y-6">
              <div className="mb-4">
                <ConsoleLogButton data={userSettings} label={t('admin.invoiceDetails.rawData')} />
              </div>
              
              {/* General Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">{t('settings.title')}</h3>
                  <div className="bg-gray-50/50 p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1">{t('settings.language')}</div>
                        <div className="text-sm font-medium">{userSettings.language}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1">{t('settings.country')}</div>
                        <div className="text-sm font-medium">{userSettings.country}</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">{t('invoice.number')}</h3>
                  <div className="bg-gray-50/50 p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1">Start Number</div>
                        <div className="text-sm font-medium">{userSettings.invoiceStartNumber}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1">Latest Number</div>
                        <div className="text-sm font-medium">{userSettings.latestInvoiceNumber}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Full JSON View */}
              <JsonViewer 
                data={userSettings} 
                title={t('admin.invoiceDetails.rawData')} 
                language={userSettings.language}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 